import React from 'react'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Paper,
  Skeleton,
} from '@mui/material'
import {
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  PhoneAndroid as PhoneIcon,
  AccountBalance as BalanceIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Apple as AppleIcon,
  Android as AndroidIcon,
} from '@mui/icons-material'

import MetricCard from '../../components/Dashboard/MetricCard'
import RevenueChart from '../../components/Dashboard/RevenueChart'
import RecentTransactions from '../../components/Dashboard/RecentTransactions'
import SystemHealth from '../../components/Dashboard/SystemHealth'
import { useDashboardData } from '../../hooks/useDashboardData'
import { formatNumber, formatCurrency } from '../../utils/formatters'

const Dashboard = () => {
  const {
    data,
    isLoading,
    error,
    refetch
  } = useDashboardData()

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <WarningIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h6" color="error" gutterBottom>
          Veri yüklenirken hata oluştu
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {error.message || 'Beklenmeyen bir hata oluştu'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
        >
          Tekrar Dene
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            SafeLine yönetim paneline hoş geldiniz
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label="Canlı Veri"
            color="success"
            size="small"
            icon={<TrendingUpIcon />}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Yenile
          </Button>
        </Box>
      </Box>

      {/* Metric Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Toplam Kullanici"
            value={data?.metrics?.totalUsers != null ? formatNumber(data.metrics.totalUsers) : '-'}
            change={data?.metrics?.userGrowth ?? 0}
            icon={<PeopleIcon />}
            color="primary"
            isLoading={isLoading}
            subtitle={
              !isLoading && (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AppleIcon sx={{ fontSize: 14, opacity: 0.9 }} />
                    <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 500 }}>
                      {data?.metrics?.iosUsers ?? 0}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AndroidIcon sx={{ fontSize: 14, opacity: 0.9, color: '#a4c639' }} />
                    <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 500 }}>
                      {data?.metrics?.androidUsers ?? 0}
                    </Typography>
                  </Box>
                </Box>
              )
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Bugunku Gelir"
            value={data?.metrics?.todayRevenue != null ? formatCurrency(data.metrics.todayRevenue) : '-'}
            change={data?.metrics?.revenueChange ?? 0}
            icon={<MoneyIcon />}
            color="success"
            isLoading={isLoading}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Aktif Kiralama"
            value={data?.metrics?.activeRentals != null ? formatNumber(data.metrics.activeRentals) : '-'}
            change={data?.metrics?.rentalChange ?? 0}
            icon={<PhoneIcon />}
            color="warning"
            isLoading={isLoading}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Platform Bakiyesi"
            value={data?.metrics?.platformBalance != null ? formatCurrency(data.metrics.platformBalance) : '-'}
            change={data?.metrics?.balanceChange ?? 0}
            icon={<BalanceIcon />}
            color="secondary"
            isLoading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Charts and Data */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Revenue Chart */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ height: 550 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Son 30 Gün Gelir Analizi
              </Typography>
              <RevenueChart
                data={data?.revenueChart || []}
                isLoading={isLoading}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* System Health */}
        <Grid item xs={12} lg={4}>
          <SystemHealth
            data={data?.systemHealth}
            isLoading={isLoading}
            error={error}
          />
        </Grid>
      </Grid>

      {/* Recent Activities */}
      <Grid container spacing={3}>
        {/* Recent Transactions */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Son İşlemler
                </Typography>
                <Button
                  size="small"
                  href="/transactions"
                  sx={{ textTransform: 'none' }}
                >
                  Tümünü Gör
                </Button>
              </Box>
              <RecentTransactions
                transactions={data?.recentTransactions || []}
                isLoading={isLoading}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Stats */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Hizli Istatistikler
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
                    {isLoading ? (
                      <>
                        <Skeleton variant="text" width="60%" height={32} sx={{ mx: 'auto' }} />
                        <Skeleton variant="text" width="80%" sx={{ mx: 'auto' }} />
                      </>
                    ) : (
                      <>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          {data?.quickStats?.successRate != null ? `${data.quickStats.successRate}%` : '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Basari Orani
                        </Typography>
                      </>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
                    {isLoading ? (
                      <>
                        <Skeleton variant="text" width="60%" height={32} sx={{ mx: 'auto' }} />
                        <Skeleton variant="text" width="80%" sx={{ mx: 'auto' }} />
                      </>
                    ) : (
                      <>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                          {data?.quickStats?.avgResponseTime != null ? `${data.quickStats.avgResponseTime}ms` : '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Ortalama Yanit
                        </Typography>
                      </>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
                    {isLoading ? (
                      <>
                        <Skeleton variant="text" width="60%" height={32} sx={{ mx: 'auto' }} />
                        <Skeleton variant="text" width="80%" sx={{ mx: 'auto' }} />
                      </>
                    ) : (
                      <>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>
                          {data?.quickStats?.pendingRequests != null ? formatNumber(data.quickStats.pendingRequests) : '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Bekleyen Istek
                        </Typography>
                      </>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f8f9fa' }}>
                    {isLoading ? (
                      <>
                        <Skeleton variant="text" width="60%" height={32} sx={{ mx: 'auto' }} />
                        <Skeleton variant="text" width="80%" sx={{ mx: 'auto' }} />
                      </>
                    ) : (
                      <>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>
                          {data?.quickStats?.errorCount != null ? formatNumber(data.quickStats.errorCount) : '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Hata Sayisi
                        </Typography>
                      </>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default Dashboard